import React, { useState } from 'react';
import { 
  Play, 
  Pause, 
  Square, 
  SkipBack, 
  SkipForward, 
  Volume2, 
  VolumeX, 
  RotateCcw, 
  Settings2, 
  X, 
  ChevronUp, 
  ChevronDown,
  Sparkles,
  BookOpen
} from 'lucide-react';
import { useVoiceReader } from '../context/VoiceReaderContext';

export const VoiceReaderBar: React.FC = () => {
  const {
    isAvailable,
    isActive,
    toggleActive,
    isPlaying,
    isPaused,
    currentText,
    currentTitle,
    currentChunkIndex,
    totalChunks,
    rate,
    setRate,
    voices,
    selectedVoice,
    setSelectedVoice,
    speakCurrentPage,
    pause,
    resume,
    stop,
    nextChunk,
    prevChunk,
  } = useVoiceReader();

  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);

  if (!isAvailable || !isActive) {
    return null;
  }

  const progressPercent = totalChunks > 0 ? Math.round(((currentChunkIndex + 1) / totalChunks) * 100) : 0;

  return (
    <aside aria-label="Spoken story narration controls" className="no-print fixed bottom-0 left-0 right-0 z-50 p-2 sm:p-4 pointer-events-none flex justify-center">
      <div className="pointer-events-auto w-full max-w-4xl bg-[#2c221e] text-[#fbf9f4] rounded-2xl sm:rounded-3xl shadow-2xl border-2 border-[#7a5b3a] overflow-hidden transition-all duration-300 backdrop-blur-md">
        
        {/* Top Mini-Header & Progress Strip */}
        <div className="bg-[#3d2f2a] px-4 py-2 flex items-center justify-between border-b border-[#544138] text-xs">
          <div className="flex items-center gap-2 font-serif text-amber-200">
            <span className="relative flex h-2.5 w-2.5">
              {isPlaying && (
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              )}
              <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${isPlaying ? 'bg-emerald-500' : 'bg-amber-400'}`}></span>
            </span>
            <span className="font-bold tracking-wide uppercase text-[11px] text-amber-300">
              Archival Voice Reader
            </span>
            {currentTitle && (
              <span className="text-amber-100/70 hidden sm:inline truncate max-w-xs">
                • {currentTitle}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {totalChunks > 0 && (
              <span className="bg-[#2c221e] px-2 py-0.5 rounded text-[11px] font-mono text-amber-200/90 border border-amber-200/20">
                {currentChunkIndex + 1} / {totalChunks} ({progressPercent}%)
              </span>
            )}

            <button
              onClick={() => setShowSettings(!showSettings)}
              className={`p-1.5 rounded-lg transition hover:bg-[#544138] ${showSettings ? 'bg-[#544138] text-amber-300' : 'text-amber-100/70'}`}
              title="Voice & Speed Settings"
            >
              <Settings2 className="w-4 h-4" />
            </button>

            <button
              onClick={toggleActive}
              className="p-1.5 rounded-lg transition hover:bg-[#544138] text-amber-200/70 hover:text-white"
              title="Close Voice Reader"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Live Progress Bar */}
        {totalChunks > 0 && (
          <div className="w-full bg-[#1e1714] h-1">
            <div 
              className="bg-emerald-500 h-1 transition-all duration-300 ease-out"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        )}

        {/* Main Controls & Live Text Display */}
        <div className="p-3 sm:p-4 space-y-3">
          
          {/* Live text display */}
          <div className="bg-[#1e1714] p-3 rounded-xl border border-[#4a3525] flex items-start gap-3 min-h-[52px]">
            <Volume2 className={`w-5 h-5 shrink-0 mt-0.5 ${isPlaying ? 'text-emerald-400 animate-pulse' : 'text-amber-400'}`} />
            <div className="flex-1 overflow-hidden">
              {currentText ? (
                <p className="font-serif text-sm md:text-base text-amber-100/95 leading-snug line-clamp-2">
                  "{currentText}"
                </p>
              ) : (
                <p className="font-serif text-xs sm:text-sm text-amber-200/60 italic">
                  Press <strong>"Read Current Page"</strong> or click any letter or story passage to start spoken narration.
                </p>
              )}
            </div>
          </div>

          {/* Settings Drawer (Speed & Voice Selection) */}
          {showSettings && (
            <div className="bg-[#241a17] p-3 rounded-xl border border-[#4a3525] grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              {/* Speed Preset */}
              <div className="space-y-1">
                <label className="text-amber-200/80 font-bold block">
                  Reading Speed
                </label>
                <div className="flex items-center gap-1.5">
                  {[
                    { label: '0.8x (Senior/Slow)', value: 0.8 },
                    { label: '1.0x (Normal)', value: 1.0 },
                    { label: '1.2x (Brisk)', value: 1.2 },
                  ].map((preset) => (
                    <button
                      key={preset.value}
                      onClick={() => setRate(preset.value)}
                      className={`flex-1 py-1 px-2 rounded-lg font-serif font-bold transition text-xs border ${
                        rate === preset.value
                          ? 'bg-[#7a5b3a] text-amber-100 border-amber-300/40'
                          : 'bg-[#3d2f2a] text-amber-200/70 border-transparent hover:bg-[#4a3525]'
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Voice Selector */}
              <div className="space-y-1">
                <label className="text-amber-200/80 font-bold block">
                  Narrator Voice
                </label>
                <select
                  value={selectedVoice?.name || ''}
                  onChange={(e) => {
                    const match = voices.find(v => v.name === e.target.value);
                    if (match) setSelectedVoice(match);
                  }}
                  className="w-full bg-[#3d2f2a] border border-[#544138] text-amber-100 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-amber-400"
                >
                  {voices.map((voice) => (
                    <option key={voice.name} value={voice.name}>
                      {voice.name} ({voice.lang})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Primary Action Buttons */}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
            
            {/* Left quick actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={speakCurrentPage}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#4a3525] hover:bg-[#634833] text-amber-100 text-xs font-serif font-bold border border-amber-200/20 transition shadow-xs"
                title="Narrate entire page from beginning"
              >
                <BookOpen className="w-3.5 h-3.5 text-amber-300" />
                <span>Read Page Aloud</span>
              </button>

              {totalChunks > 0 && (
                <button
                  onClick={stop}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-[#3d2f2a] hover:bg-rose-900/60 text-rose-200 text-xs font-serif font-bold transition border border-rose-400/20"
                  title="Stop reading"
                >
                  <Square className="w-3.5 h-3.5" />
                  <span>Stop</span>
                </button>
              )}
            </div>

            {/* Center Playback Controls */}
            <div className="flex items-center gap-1.5 sm:gap-2">
              <button
                onClick={prevChunk}
                disabled={totalChunks === 0 || currentChunkIndex === 0}
                className="p-2 rounded-xl bg-[#3d2f2a] hover:bg-[#544138] text-amber-100 disabled:opacity-40 disabled:hover:bg-[#3d2f2a] transition"
                title="Previous Sentence"
              >
                <SkipBack className="w-4 h-4" />
              </button>

              {isPlaying ? (
                <button
                  onClick={pause}
                  className="flex items-center gap-1.5 px-5 py-2 rounded-2xl bg-amber-500 hover:bg-amber-400 text-[#2c221e] font-serif font-bold text-sm transition shadow-md"
                  title="Pause Narration"
                >
                  <Pause className="w-5 h-5 fill-current" />
                  <span>Pause</span>
                </button>
              ) : isPaused ? (
                <button
                  onClick={resume}
                  className="flex items-center gap-1.5 px-5 py-2 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-serif font-bold text-sm transition shadow-md"
                  title="Resume Narration"
                >
                  <Play className="w-5 h-5 fill-current" />
                  <span>Resume</span>
                </button>
              ) : (
                <button
                  onClick={speakCurrentPage}
                  className="flex items-center gap-1.5 px-5 py-2 rounded-2xl bg-amber-500 hover:bg-amber-400 text-[#2c221e] font-serif font-bold text-sm transition shadow-md"
                  title="Start Narration"
                >
                  <Play className="w-5 h-5 fill-current" />
                  <span>Listen Now</span>
                </button>
              )}

              <button
                onClick={nextChunk}
                disabled={totalChunks === 0 || currentChunkIndex >= totalChunks - 1}
                className="p-2 rounded-xl bg-[#3d2f2a] hover:bg-[#544138] text-amber-100 disabled:opacity-40 disabled:hover:bg-[#3d2f2a] transition"
                title="Next Sentence"
              >
                <SkipForward className="w-4 h-4" />
              </button>
            </div>

            {/* Right Status Indicator */}
            <div className="hidden sm:flex items-center gap-2 text-xs font-serif text-amber-200/80">
              <span className="px-2.5 py-1 bg-[#1e1714] rounded-lg border border-[#4a3525]">
                Speed: <strong>{rate}x</strong>
              </span>
            </div>

          </div>

        </div>

      </div>
    </aside>
  );
};
